import { DocumentNode, useMutation } from '@apollo/client';
import {
  BottomNavigation,
  Button,
  FileUploader,
  FormDirtyModal,
  Tabs,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PartialResourcePage, ResourceForm as Form } from './types';
import {
  mapResourceToForm,
  mapVideoFormToResource,
} from 'src/components/resource/form/utils';
import {
  CreateResourceDocument,
  ResourcePageType,
  UpdateResourceDocument,
  UploadResourceVideoDocument,
} from 'src/graphql/generated/graphql';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';

import { PermissionField } from './fields/permission';
import { GeneralInfoField } from './fields/general-info';
import { ResourceUploaderContent } from './resource-uploader-content';
import { VideosTable } from './videos-table';
import { VideoResourceItem } from './types';
import { EditDescriptionModal } from './edit-description-modal';

type VideoFormProps = {
  resource?: PartialResourcePage;
  refetchQueries?: DocumentNode[];
  onCancel: () => void;
  onSave: () => void;
};

enum TabType {
  Settings = 'settings',
  Video = 'video',
}

export const VideoForm = ({
  resource,
  refetchQueries = [],
  onCancel: onCancelProp,
  onSave: onSaveProp,
}: VideoFormProps) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(TabType.Settings);

  const [resourceItems, setResourceItems] = useState<VideoResourceItem[]>([]);
  const [resourceItemsUpdatedCount, setResourceItemsUpdatedCount] = useState(0);
  const [editingResourceItem, setEditingResourceItem] = useState<
    VideoResourceItem | null
  >(null);

  const [displayFormDirtyModal, setDisplayFormDirtyModal] = useState(false);

  const isUploading = useMemo(() => {
    return resourceItems.some((item) => item.isUploading);
  }, [resourceItems]);

  const tabs = [
    { label: t('resources.form.tabs.settings'), value: TabType.Settings },
    { label: t('resources.form.tabs.video'), value: TabType.Video },
  ];

  const [createResource, { loading: loadingCreate }] = useMutation(
    CreateResourceDocument,
  );
  const [updateResource, { loading: loadingUpdate }] = useMutation(
    UpdateResourceDocument,
  );
  const loading = loadingCreate || loadingUpdate;

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<Form>({
    defaultValues: resource ? mapResourceToForm(resource) : {},
  });

  useEffect(() => {
    reset(resource ? mapResourceToForm(resource) : {});
    setResourceItems(resource?.items || []);
  }, [resource]);

  const onSubmit = async (data: Form) => {
    const formData = mapVideoFormToResource({
      ...data,
      items: resourceItems,
    });

    if (!formData) {
      return;
    }

    if (resource?.id) {
      await updateResource({
        variables: {
          id: resource.id,
          data: {
            ...formData,
          },
        },
        refetchQueries,
      });
    } else {
      await createResource({
        variables: {
          data: formData,
        },
        refetchQueries,
      });
    }

    onSaveProp();
  };

  const onCancel = () => {
    if (isDirty || resourceItemsUpdatedCount > 0) {
      setDisplayFormDirtyModal(true);
    } else {
      onCancelProp();
    }
  };

  const [upload] = useMutation(UploadResourceVideoDocument);

  const onFileSubmit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setResourceItemsUpdatedCount((prev) => prev + 1);

    const resourceItem = {
      description: file.name,
      file: file,
      isUploading: true,
    };

    setResourceItems((prev) => [...prev, resourceItem]);
    setEditingResourceItem(resourceItem);

    const { data } = await upload({
      variables: {
        file,
      },
    });

    if (!data?.uploadResourceVideo) {
      return;
    }

    setResourceItems((prev) =>
      prev.map((item) =>
        item.file === file
          ? {
              ...item,
              isUploading: false,
              externalItem: {
                value: data?.uploadResourceVideo,
                id: data?.uploadResourceVideo,
              },
            }
          : item,
      ),
    );
  };

  const onResourceItemsSorted = (rows: any) => {
    setResourceItems(rows);
    setResourceItemsUpdatedCount((prev) => prev + 1);
  };

  const onResourceItemChange = (
    updatedItem: VideoResourceItem,
    { description }: { description: string },
  ) => {
    setResourceItems(
      resourceItems.map((item) =>
        item === updatedItem ? { ...item, description } : item,
      ),
    );
    setResourceItemsUpdatedCount((prev) => prev + 1);
    setEditingResourceItem(null);
  };

  const onResourceItemDelete = (item: VideoResourceItem) => {
    setResourceItems(resourceItems.filter((i) => i !== item));
    setResourceItemsUpdatedCount((prev) => prev + 1);
  };

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <TableWrapper className="mt-6">
          <Tabs
            tabs={tabs}
            onChange={(_, value) => setActiveTab(value)}
            value={activeTab}
          />
          {activeTab === TabType.Video && (
            <div className="mt-4 mb-4">
              {resourceItems.length > 0 && (
                <VideosTable
                  key={resourceItemsUpdatedCount}
                  data={resourceItems}
                  onChange={onResourceItemChange}
                  onDelete={onResourceItemDelete}
                  isRowsSortable={true}
                  onRowsSorted={onResourceItemsSorted}
                  options={{
                    count: resourceItems.length,
                  }}
                />
              )}

              <div className="mt-4">
                <FileUploader
                  error={!!errors.fileAsset}
                  onUploadFiles={onFileSubmit}
                  fileAccept={['video/*']}
                  buttonContent={() => (
                    <ResourceUploaderContent type={ResourcePageType.Video} />
                  )}
                  buttonFullWidth={true}
                  fileDescription={false}
                  maxFileCount={1}
                  showLoader={false}
                />
              </div>
            </div>
          )}

          {activeTab === TabType.Settings && (
            <GeneralInfoField control={control} errors={errors} />
          )}
        </TableWrapper>

        <TableWrapper className="mt-6">
          {activeTab === TabType.Settings && (
            <PermissionField control={control} />
          )}

          <BottomNavigation>
            <Button
              className="mr-2"
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              disabled={loading || isUploading}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={onCancel}
              className="mr-4"
            >
              {t('common.close')}
            </Button>
          </BottomNavigation>
        </TableWrapper>

        <FormDirtyModal
          open={displayFormDirtyModal}
          onClose={() => setDisplayFormDirtyModal(false)}
          onCloseWithoutSaving={onCancelProp}
        />

        {editingResourceItem && (
          <EditDescriptionModal
            open={true}
            onClose={() => setEditingResourceItem(null)}
            onSave={({ description }) =>
              onResourceItemChange(editingResourceItem, { description })
            }
            defaultValues={{
              description: editingResourceItem.description || '',
            }}
          />
        )}
      </form>
    </ContentWrapper>
  );
};
